import { AxiosError } from 'axios';
import { axiosInstance } from './axios-instance';
import type {
  PlaygroundClassifiedError,
  PlaygroundErrorType,
  PlaygroundInvokeResult,
  PlaygroundItemsResult,
  PlaygroundObjectsResult,
} from './types';

const BASE_PATH = '/admin/integrations-playground';

export interface PlaygroundObjectsParams {
  accountId: string;
  integrationKey: string;
  batchSize?: number;
  cursor?: string;
}

export interface PlaygroundItemsIdsRequest {
  accountId: string;
  integrationKey: string;
  objectType: string;
  ids: string[];
}

export interface PlaygroundItemsDateRangeRequest {
  accountId: string;
  integrationKey: string;
  objectType: string;
  fromDate: string;
  toDate: string;
  dateField?: 'createdAt' | 'updatedAt';
  maxItems?: number;
  extraParams?: Record<string, unknown>;
}

export interface PlaygroundInvokeRequest {
  accountId: string;
  integrationKey: string;
  method: string;
  args?: Record<string, unknown>;
}

export class IntegrationsPlaygroundService {
  static async getObjects(params: PlaygroundObjectsParams): Promise<PlaygroundObjectsResult> {
    const { accountId, integrationKey, batchSize, cursor } = params;
    const res = await axiosInstance.get<PlaygroundObjectsResult>(`${BASE_PATH}/objects`, {
      params: { accountId, integrationKey, batchSize, cursor },
    });
    return res.data;
  }

  static async getItemsByIds(request: PlaygroundItemsIdsRequest): Promise<PlaygroundItemsResult> {
    const { accountId, integrationKey, objectType, ids } = request;
    const res = await axiosInstance.get<PlaygroundItemsResult>(`${BASE_PATH}/items`, {
      params: { accountId, integrationKey, objectType, ids: ids.join(',') },
    });
    return res.data;
  }

  static async getItemsByDateRange(
    request: PlaygroundItemsDateRangeRequest,
  ): Promise<PlaygroundItemsResult> {
    const {
      accountId,
      integrationKey,
      objectType,
      fromDate,
      toDate,
      dateField,
      maxItems,
      extraParams,
    } = request;
    const res = await axiosInstance.get<PlaygroundItemsResult>(`${BASE_PATH}/items`, {
      params: {
        accountId,
        integrationKey,
        objectType,
        fromDate,
        toDate,
        dateField,
        maxItems,
        extraParams: extraParams ? JSON.stringify(extraParams) : undefined,
      },
    });
    return res.data;
  }

  static async invoke(request: PlaygroundInvokeRequest): Promise<PlaygroundInvokeResult> {
    const res = await axiosInstance.post<PlaygroundInvokeResult>(`${BASE_PATH}/invoke`, request);
    return res.data;
  }
}

export interface DisplayablePlaygroundError {
  errorType?: PlaygroundErrorType;
  message: string;
}

function isClassifiedError(value: unknown): value is PlaygroundClassifiedError {
  return typeof value === 'object' && value !== null && 'originError' in value;
}

export function extractPlaygroundError(error: unknown): DisplayablePlaygroundError {
  const responseData = (error as AxiosError<{ error?: unknown }>)?.response?.data;
  const embedded = responseData?.error;
  if (isClassifiedError(embedded)) {
    return { errorType: embedded.errorType, message: embedded.originError };
  }
  if (typeof embedded === 'string') {
    return { message: embedded };
  }
  return { message: (error as Error)?.message ?? 'Unknown error' };
}
